/* eslint-disable no-use-before-define */
/* eslint-disable react/prop-types */
import React, { useEffect } from 'react';
import { useLocation } from 'react-router';
import { Link as RouterLink } from 'react-router-dom';
import PropTypes from 'prop-types';
import {
  Box,
  Drawer,
  Hidden,
  List,
  Link,
  makeStyles,
  Typography
} from '@material-ui/core';
import { alpha } from '@material-ui/core/styles';
import Logo from 'src/components/Logo';
import NavItem from './NavItem';
import Manual from 'src/assets/files/manual.pdf';
import { useTranslation } from 'react-i18next';

function renderNavItems({ items, ...rest }) {
  return (
    <List disablePadding>
      {items.reduce(
        (acc, item) => reduceChildRoutes({ acc, item, ...rest }),
        []
      )}
    </List>
  );
}

function reduceChildRoutes({ acc, item, depth = 0 }) {
  if (item.items) {
    acc.push(
      <NavItem depth={depth} key={item.href} title={item.title}>
        {renderNavItems({
          depth: depth + 1,
          items: item.items
        })}
      </NavItem>
    );
  } else {
    acc.push(
      <NavItem
        depth={depth}
        href={item.href}
        key={item.href}
        title={item.title}
      />
    );
  }

  return acc;
}

const useStyles = makeStyles(theme => ({
  mobilePaper: {
    width: 280,
    backgroundColor: theme.palette.background.paper,
    borderRight: `1px solid ${theme.palette.divider}`
  },
  desktopPaper: {
    width: 280,
    top: 64,
    height: 'calc(100% - 64px)',
    backgroundColor: theme.palette.background.paper,
    borderRight: `1px solid ${theme.palette.divider}`
  },
  navHeader: {
    padding: 16,
    borderBottom: `1px solid ${theme.palette.divider}`,
    background:
      `linear-gradient(180deg, ${alpha(theme.palette.primary.main, 0.08)} 0%, transparent 100%)`
  },
  navTitle: {
    fontWeight: 700,
    letterSpacing: 0.2,
    marginBottom: 4
  },
  navSubtitle: {
    color: theme.palette.text.secondary,
    fontSize: 12,
    lineHeight: 1.5
  },
  navBody: {
    padding: 8
  },
  helpCard: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 16,
    backgroundColor: alpha(
      theme.palette.secondary.main,
      theme.palette.type === 'light' ? 0.05 : 0.12
    ),
    padding: 16
  },
  helpLink: {
    display: 'inline-flex',
    marginTop: 8,
    fontWeight: 600
  }
}));

function NavBar({ openMobile, onMobileClose }) {
  const classes = useStyles();
  const location = useLocation();
  const { t } = useTranslation('common');

  const navItems = [
    {
      title: t('welcomeTitle'),
      href: '/docs'
    },
    {
      title: t('gettingStartedTitle'),
      href: '/docs/getting-started'
    },
    {
      title: t('adminDashboardTitle'),
      href: '/docs/admin'
    },
    {
      title: t('studioDashboardTitle'),
      href: '/docs/studio'
    },

    {
      title: t('humanHelpTitle'),
      href: '/docs/human-help'
    },

    {
      title: t('actionsTitle'),
      href: '/docs/actions'
    },
    {
      title: t('messagesTitle'),
      href: '/docs/messages'
    },
    {
      title: t('settingsTitle'),
      href: '/docs/settings'
    },

    {
      title: t('nluConfigTitle'),
      href: '/docs/nlu-config'
    },
    {
      title: t('llmConfigTitle'),
      href: '/docs/llm-config'
    },
    {
      title: t('llmPlaygroundTitle'),
      href: '/docs/llm-playground'
    },

    {
      title: t('channelsRestrictionsTitle'),
      href: '/docs/channels-restrictions'
    },
    {
      title: t('channelsConfiguraationTitle'),
      href: '/docs/channels-config'
    },
    // {
    //   title: 'Authentication',
    //   href: '/docs/authentication'
    // },

    {
      title: t('changelogTitle'),
      href: '/docs/changelog'
    }
  ];
  useEffect(() => {
    if (openMobile && onMobileClose) {
      onMobileClose();
    }
    // eslint-disable-next-line
  }, [location.pathname]);

  const content = (
    <Box height='100%' display='flex' flexDirection='column'>
      <Hidden lgUp>
        <Box className={classes.navHeader}>
          <RouterLink to='/'>
            <Logo />
          </RouterLink>
          <Typography className={classes.navTitle} variant='subtitle1'>
            {t('documentation', { ns: 'common' })}
          </Typography>
          <Typography className={classes.navSubtitle}>
            {t('help')}
          </Typography>
        </Box>
      </Hidden>
      <Box className={classes.navHeader} display={{ xs: 'none', lg: 'block' }}>
        <Typography className={classes.navTitle} variant='subtitle1'>
          {t('documentation', { ns: 'common' })}
        </Typography>
        <Typography className={classes.navSubtitle}>
          {t('help')}
        </Typography>
      </Box>
      <Box className={classes.navBody}>
        {renderNavItems({ items: navItems })}
      </Box>
      <Box p={2} pt={0}>
        <Box className={classes.helpCard}>
          <Typography variant='h6' color='textPrimary'>
            {t('help')}
          </Typography>
          <Link
            className={classes.helpLink}
            variant='subtitle1'
            color='secondary'
            href={Manual}
            target='_blank'
            download
          >
            {t('downloadManual')}
          </Link>
        </Box>
      </Box>
    </Box>
  );

  return (
    <>
      <Hidden lgUp>
        <Drawer
          anchor='left'
          classes={{ paper: classes.mobilePaper }}
          onClose={onMobileClose}
          open={openMobile}
          variant='temporary'
        >
          {content}
        </Drawer>
      </Hidden>
      <Hidden mdDown>
        <Drawer
          anchor='left'
          classes={{ paper: classes.desktopPaper }}
          open
          variant='persistent'
        >
          {content}
        </Drawer>
      </Hidden>
    </>
  );
}

NavBar.propTypes = {
  onMobileClose: PropTypes.func,
  openMobile: PropTypes.bool
};

export default NavBar;
